import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Plus, Bed, Filter, MoreVertical } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
    available: "Available", occupied: "Occupied", dirty: "Dirty",
    cleaning: "Cleaning", reserved: "Reserved", maintenance: "Maintenance", out_of_order: "Out of Order",
};

const STATUS_DOT = {
    available: "bg-emerald-500", occupied: "bg-blue-500", dirty: "bg-red-500",
    cleaning: "bg-yellow-500", reserved: "bg-indigo-500", maintenance: "bg-orange-500", out_of_order: "bg-orange-500",
};

export default function Rooms() {
    const { activeHotelId, user } = useAuth();
    const [rooms, setRooms] = useState([]);
    const [types, setTypes] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [openRoom, setOpenRoom] = useState(false);
    const [openType, setOpenType] = useState(false);
    const [roomForm, setRoomForm] = useState({ number: "", room_type_id: "", floor: 1, view: "", smoking: false });
    const [typeForm, setTypeForm] = useState({ name: "", code: "", base_rate: 3000, max_occupancy: 2 });

    const canEdit = ["super_admin", "hotel_owner", "general_manager"].includes(user?.role);

    const load = async () => {
        if (!activeHotelId) return;
        setLoading(true);
        try {
            const [r, t] = await Promise.all([
                api.get(`/rooms?hotel_id=${activeHotelId}`),
                api.get(`/rooms/types?hotel_id=${activeHotelId}`),
            ]);
            setRooms(r.data);
            setTypes(t.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const changeStatus = async (room, status) => {
        try {
            await api.patch(`/rooms/${room.id}/status`, { status });
            toast.success(`Room ${room.number} → ${STATUS_LABELS[status]}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const createRoom = async () => {
        try {
            await api.post("/rooms", { ...roomForm, hotel_id: activeHotelId, floor: Number(roomForm.floor) });
            toast.success("Room created");
            setOpenRoom(false);
            setRoomForm({ number: "", room_type_id: "", floor: 1, view: "", smoking: false });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const createType = async () => {
        try {
            await api.post("/rooms/types", { ...typeForm, hotel_id: activeHotelId, base_rate: Number(typeForm.base_rate), max_occupancy: Number(typeForm.max_occupancy) });
            toast.success("Room type created");
            setOpenType(false);
            setTypeForm({ name: "", code: "", base_rate: 3000, max_occupancy: 2 });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const filtered = useMemo(() => filter === "all" ? rooms : rooms.filter(r => r.status === filter), [rooms, filter]);
    const groupedByFloor = useMemo(() => {
        const g = {};
        filtered.forEach(r => { (g[r.floor] = g[r.floor] || []).push(r); });
        return g;
    }, [filtered]);

    const statusCounts = useMemo(() => {
        const c = {};
        rooms.forEach(r => { c[r.status] = (c[r.status] || 0) + 1; });
        return c;
    }, [rooms]);

    return (
        <AppShell title="Rooms" subtitle={`${rooms.length} rooms · ${types.length} room types`}>
            {/* Filter Bar */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <button onClick={() => setFilter("all")}
                    className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${filter === "all" ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                    data-testid="filter-all">
                    All <span className="ml-1 opacity-70">({rooms.length})</span>
                </button>
                {Object.keys(STATUS_LABELS).map(s => (
                    <button key={s} onClick={() => setFilter(s)}
                        className={`px-3 py-1.5 text-xs rounded-sm border transition-colors flex items-center gap-1.5 ${filter === s ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                        data-testid={`filter-${s}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s]}`} />
                        {STATUS_LABELS[s]} <span className="ml-1 opacity-70">({statusCounts[s] || 0})</span>
                    </button>
                ))}

                <div className="ml-auto flex gap-2">
                    {canEdit && (
                        <>
                            <Dialog open={openType} onOpenChange={setOpenType}>
                                <DialogTrigger asChild>
                                    <Button variant="outline" className="rounded-sm" data-testid="add-room-type-btn">+ Type</Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader><DialogTitle>New Room Type</DialogTitle></DialogHeader>
                                    <div className="space-y-3">
                                        <div><Label className="text-xs uppercase tracking-wider">Name</Label>
                                            <Input value={typeForm.name} onChange={e => setTypeForm({ ...typeForm, name: e.target.value })} data-testid="type-name" /></div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><Label className="text-xs uppercase tracking-wider">Code</Label>
                                                <Input value={typeForm.code} onChange={e => setTypeForm({ ...typeForm, code: e.target.value })} /></div>
                                            <div><Label className="text-xs uppercase tracking-wider">Base Rate</Label>
                                                <Input type="number" value={typeForm.base_rate} onChange={e => setTypeForm({ ...typeForm, base_rate: e.target.value })} /></div>
                                        </div>
                                        <div><Label className="text-xs uppercase tracking-wider">Max Occupancy</Label>
                                            <Input type="number" value={typeForm.max_occupancy} onChange={e => setTypeForm({ ...typeForm, max_occupancy: e.target.value })} /></div>
                                    </div>
                                    <DialogFooter><Button onClick={createType} className="bg-[#0A1C3E]" data-testid="type-submit">Create</Button></DialogFooter>
                                </DialogContent>
                            </Dialog>

                            <Dialog open={openRoom} onOpenChange={setOpenRoom}>
                                <DialogTrigger asChild>
                                    <Button className="bg-[#0A1C3E] hover:bg-[#132D5C] rounded-sm" data-testid="add-room-btn"><Plus className="w-4 h-4 mr-1" /> Room</Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader><DialogTitle>New Room</DialogTitle></DialogHeader>
                                    <div className="space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div><Label className="text-xs uppercase tracking-wider">Room number</Label>
                                                <Input value={roomForm.number} onChange={e => setRoomForm({ ...roomForm, number: e.target.value })} data-testid="room-number" /></div>
                                            <div><Label className="text-xs uppercase tracking-wider">Floor</Label>
                                                <Input type="number" value={roomForm.floor} onChange={e => setRoomForm({ ...roomForm, floor: e.target.value })} /></div>
                                        </div>
                                        <div><Label className="text-xs uppercase tracking-wider">Room type</Label>
                                            <Select value={roomForm.room_type_id} onValueChange={v => setRoomForm({ ...roomForm, room_type_id: v })}>
                                                <SelectTrigger data-testid="room-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                                                <SelectContent>
                                                    {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name} · ₹{t.base_rate}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label className="text-xs uppercase tracking-wider">View</Label>
                                            <Input value={roomForm.view} onChange={e => setRoomForm({ ...roomForm, view: e.target.value })} placeholder="Sea / City / Garden" /></div>
                                    </div>
                                    <DialogFooter><Button onClick={createRoom} disabled={!roomForm.number || !roomForm.room_type_id} className="bg-[#0A1C3E]" data-testid="room-submit">Create</Button></DialogFooter>
                                </DialogContent>
                            </Dialog>
                        </>
                    )}
                </div>
            </div>

            {/* Room grid grouped by floor */}
            {loading ? (
                <div className="text-slate-500 text-sm">Loading...</div>
            ) : (
                <div className="space-y-6">
                    {Object.keys(groupedByFloor).sort().map(floor => (
                        <div key={floor}>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Floor {floor}</div>
                            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                {groupedByFloor[floor].map(room => (
                                    <DropdownMenu key={room.id}>
                                        <DropdownMenuTrigger asChild>
                                            <div className={`room-cell ${room.status}`} data-testid={`room-${room.number}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="font-mono text-sm font-bold text-slate-900">{room.number}</div>
                                                    <Bed className="w-3.5 h-3.5 text-slate-400" />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] text-slate-600 truncate">{room.room_type?.name}</div>
                                                    <div className="mt-1 flex items-center gap-1">
                                                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[room.status]}`} />
                                                        <span className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider">{STATUS_LABELS[room.status]}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent>
                                            <DropdownMenuLabel>Set Status</DropdownMenuLabel>
                                            {Object.keys(STATUS_LABELS).map(s => (
                                                <DropdownMenuItem key={s} onClick={() => changeStatus(room, s)} data-testid={`room-${room.number}-set-${s}`}>
                                                    <span className={`w-2 h-2 rounded-full ${STATUS_DOT[s]} mr-2`} /> {STATUS_LABELS[s]}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </AppShell>
    );
}
